import { RefObject } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Code,
  Code2,
  Quote,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface MessageFormatToolbarProps {
  textareaRef: RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  className?: string;
}

type WrapFn = (sel: string) => { before: string; after: string; placeholder?: string };
type LinePrefixFn = (lineIdx: number) => string;

export const MessageFormatToolbar = ({
  textareaRef,
  value,
  onChange,
  disabled,
  className,
}: MessageFormatToolbarProps) => {
  const wrapSelection = (fn: WrapFn) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = value.slice(start, end);
    const { before, after, placeholder } = fn(sel);
    const inner = sel || placeholder || "";
    const next = value.slice(0, start) + before + inner + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursorStart = start + before.length;
      const cursorEnd = cursorStart + inner.length;
      ta.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const prefixLines = (fn: LinePrefixFn) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    // Expand selection to full lines
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const lineEndRaw = value.indexOf("\n", end);
    const lineEnd = lineEndRaw === -1 ? value.length : lineEndRaw;
    const block = value.slice(lineStart, lineEnd);
    const lines = block.length ? block.split("\n") : [""];
    const prefixed = lines.map((ln, i) => `${fn(i)}${ln}`).join("\n");
    const next = value.slice(0, lineStart) + prefixed + value.slice(lineEnd);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(lineStart, lineStart + prefixed.length);
    });
  };

  const actions = [
    {
      label: "Bold (Ctrl/Cmd+B)",
      icon: Bold,
      run: () => wrapSelection(() => ({ before: "**", after: "**", placeholder: "bold" })),
    },
    {
      label: "Italic (Ctrl/Cmd+I)",
      icon: Italic,
      run: () => wrapSelection(() => ({ before: "*", after: "*", placeholder: "italic" })),
    },
    {
      label: "Strikethrough",
      icon: Strikethrough,
      run: () => wrapSelection(() => ({ before: "~~", after: "~~", placeholder: "strike" })),
    },
    {
      label: "Quote",
      icon: Quote,
      run: () => prefixLines(() => "> "),
    },
    {
      label: "Bulleted list",
      icon: List,
      run: () => prefixLines(() => "- "),
    },
    {
      label: "Numbered list",
      icon: ListOrdered,
      run: () => prefixLines((i) => `${i + 1}. `),
    },
    {
      label: "Inline code",
      icon: Code,
      run: () => wrapSelection(() => ({ before: "`", after: "`", placeholder: "code" })),
    },
    {
      label: "Code block",
      icon: Code2,
      run: () =>
        wrapSelection((sel) => ({
          before: "```\n",
          after: "\n```",
          placeholder: sel || "code block",
        })),
    },
    {
      label: "Link (Ctrl/Cmd+K)",
      icon: LinkIcon,
      run: () =>
        wrapSelection((sel) => ({
          before: "[",
          after: "](https://)",
          placeholder: sel || "link text",
        })),
    },
  ];

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-0.5 px-1 py-1 border border-b-0 rounded-t-md bg-muted/30",
        className
      )}
    >
      {actions.map(({ label, icon: Icon, run }) => (
        <Tooltip key={label}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={run}
              disabled={disabled}
              aria-label={label}
            >
              <Icon className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{label}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
};

/**
 * Keyboard shortcut handler for use in a textarea onKeyDown.
 * Returns true if it handled the event (caller should not run default behavior).
 * Supports Ctrl/Cmd + B / I / K.
 */
export function handleFormatShortcuts(
  e: React.KeyboardEvent<HTMLTextAreaElement>,
  textareaRef: RefObject<HTMLTextAreaElement>,
  value: string,
  onChange: (v: string) => void
): boolean {
  if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return false;
  const key = e.key.toLowerCase();
  if (!["b", "i", "k"].includes(key)) return false;
  e.preventDefault();
  const ta = textareaRef.current;
  if (!ta) return true;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const sel = value.slice(start, end);
  let before = "",
    after = "",
    placeholder = "";
  if (key === "b") {
    before = "**";
    after = "**";
    placeholder = "bold";
  } else if (key === "i") {
    before = "*";
    after = "*";
    placeholder = "italic";
  } else {
    before = "[";
    after = "](https://)";
    placeholder = sel || "link text";
  }
  const inner = sel || placeholder;
  const next = value.slice(0, start) + before + inner + after + value.slice(end);
  onChange(next);
  requestAnimationFrame(() => {
    ta.focus();
    const cursorStart = start + before.length;
    ta.setSelectionRange(cursorStart, cursorStart + inner.length);
  });
  return true;
}